import { ViewBasic } from '@quatrecentquatre/manage-me';

// px/second - constant scroll speed regardless of how many images are in the carousel, so a
// carousel with 6 images and one with 20 images both feel like the same continuous ticker.
const SCROLL_SPEED = 60;

export class PagebuilderCarousel extends ViewBasic {
    constructor(options) {
        super(options);

        this.wrapperEl = this.el.querySelector('.carousel-wrapper');
        this.trackEl = this.el.querySelector('.carousel-track');
        this.resizeObserver = null;
        this.isDuplicated = false;

        this.handleResize = this.handleResize.bind(this);
    }

    initialize() {
        super.initialize();

        if (!this.trackEl) {
            return;
        }

        this.originalItems = Array.from(this.trackEl.children);
        this.updateScrolling();
    }

    addEvents() {
        super.addEvents();

        if (window.ResizeObserver && this.wrapperEl) {
            this.resizeObserver = new ResizeObserver(this.handleResize);
            this.resizeObserver.observe(this.wrapperEl);
        } else {
            window.addEventListener('resize', this.handleResize);
        }
    }

    removeEvents() {
        super.removeEvents();

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        } else {
            window.removeEventListener('resize', this.handleResize);
        }
    }

    handleResize() {
        this.updateScrolling();
    }

    // Continuous scroll only makes sense (and only gets duplicated content, for a seamless
    // loop) when the images actually overflow the wrapper - otherwise they just sit still,
    // same reasoning as the previous watchOverflow-based behavior.
    updateScrolling() {
        if (!this.trackEl || !this.wrapperEl) {
            return;
        }

        this.resetTrack();

        // scrollWidth (measured on the single, un-duplicated track) naturally includes the
        // flex gaps between items, unlike summing each item's own rect width - a previous
        // version did that and under-counted the row's real width by the total gap space,
        // which made overflowing rows wrongly register as "fits, no need to scroll".
        const contentWidth = this.trackEl.scrollWidth;
        const overflows = contentWidth > this.wrapperEl.clientWidth;

        this.trackEl.classList.toggle('is-scrolling', overflows);

        if (!overflows) {
            this.trackEl.style.removeProperty('animation-duration');
            return;
        }

        this.duplicateTrack();
        this.trackEl.style.setProperty('animation-duration', `${contentWidth / SCROLL_SPEED}s`);
    }

    duplicateTrack() {
        if (this.isDuplicated) {
            return;
        }

        const fragment = document.createDocumentFragment();
        this.originalItems.forEach((item) => {
            fragment.appendChild(item.cloneNode(true));
        });
        this.trackEl.appendChild(fragment);
        this.isDuplicated = true;
    }

    resetTrack() {
        if (!this.isDuplicated) {
            return;
        }

        Array.from(this.trackEl.children).forEach((child) => {
            if (!this.originalItems.includes(child)) {
                child.remove();
            }
        });
        this.isDuplicated = false;
    }
}

Me.views['PagebuilderCarousel'] = PagebuilderCarousel;
